"use client"

import * as React from "react"
import { Calendar } from "@/components/ui/calendar"
import { ChevronDown, Minus, Maximize2, Pin, PanelLeft, PanelTop, PanelRight, X, MapPin, RefreshCw } from "lucide-react"
import { buildWidgetSrcDoc } from "@/lib/widget-bridge"
import { useWeather } from "@/hooks/useWeather"
import { CalculatorWidget } from "@/components/widgets/CalculatorWidget"
import { CalendarApp } from "@/components/widgets/CalendarApp"

interface FloatingWidgetContainerProps {
  activeFloatingWidgets: string[]
  widgetsConfig: any[]
  floatingPositions: Record<string, { x: number; y: number; z: number }>
  floatingSizes: Record<string, { w: number; h: number }>
  setFloatingPositions: React.Dispatch<React.SetStateAction<Record<string, { x: number; y: number; z: number }>>>
  setFloatingSizes: React.Dispatch<React.SetStateAction<Record<string, { w: number; h: number }>>>
  minimizedWidgets: Set<string>
  handleFocusFloatingWidget: (id: string) => void
  handleDragWidget: (id: string, x: number, y: number) => void
  handleMinimizeWidget: (id: string) => void
  handlePinWidget: (id: string) => void
  handleToggleFloatingWidget: (id: string) => void
  calcFocusedWidgetId: string | null
  setCalcFocusedWidgetId: (id: string | null) => void
  theme: string | undefined
  lang?: string
  pinnedWidgets?: string[]
  pinnedWidgetSides?: Record<string, "left" | "right" | "top">
  setPinnedWidgetSides?: React.Dispatch<React.SetStateAction<Record<string, "left" | "right" | "top">>>
  setCollapsedSides?: React.Dispatch<React.SetStateAction<Record<"left" | "right" | "top", boolean>>>
  // Calculator states & handlers
  calcFormula: string
  setCalcFormula: React.Dispatch<React.SetStateAction<string>>
  calcDisplay: string
  setCalcDisplay: React.Dispatch<React.SetStateAction<string>>
  showCalcHistory: boolean
  setShowCalcHistory: React.Dispatch<React.SetStateAction<boolean>>
  calcHistory: Array<{ expr: string; result: string }>
  setCalcHistory: React.Dispatch<React.SetStateAction<Array<{ expr: string; result: string }>>>
  setCalcResetOnNext: React.Dispatch<React.SetStateAction<boolean>>
  handleCalcPress: (val: string) => void
  // Calendar states
  calendarDate: Date | undefined
  setCalendarDate: (date: Date | undefined) => void
  // Notes states
  notesContent: string
  setNotesContent: (content: string) => void
}

export function FloatingWidgetContainer({
  activeFloatingWidgets,
  widgetsConfig,
  floatingPositions,
  floatingSizes,
  setFloatingPositions,
  setFloatingSizes,
  minimizedWidgets,
  handleFocusFloatingWidget,
  handleDragWidget,
  handleMinimizeWidget,
  handlePinWidget,
  handleToggleFloatingWidget,
  calcFocusedWidgetId,
  setCalcFocusedWidgetId,
  theme,
  lang = "pt",
  pinnedWidgets,
  pinnedWidgetSides,
  setPinnedWidgetSides,
  setCollapsedSides,
  calcFormula,
  setCalcFormula,
  calcDisplay,
  setCalcDisplay,
  showCalcHistory,
  setShowCalcHistory,
  calcHistory,
  setCalcHistory,
  setCalcResetOnNext,
  handleCalcPress,
  calendarDate,
  setCalendarDate,
  notesContent,
  setNotesContent
}: FloatingWidgetContainerProps) {
  const [openPinPickerId, setOpenPinPickerId] = React.useState<string | null>(null)

  const pinWidgetToSide = (widgetId: string, targetSide: "left" | "right" | "top") => {
    if (setPinnedWidgetSides) {
      setPinnedWidgetSides((prev) => ({ ...prev, [widgetId]: targetSide }))
    }
    if (setCollapsedSides) {
      setCollapsedSides((prev) => ({ ...prev, [targetSide]: false }))
    }
    if (!pinnedWidgets?.includes(widgetId)) {
      handlePinWidget(widgetId)
    }
    setOpenPinPickerId(null)
  }

  return (
    <>
      {activeFloatingWidgets.map((widgetId) => {
        const widget = widgetsConfig.find((w) => w.id === widgetId)
        if (!widget) return null

        const pos = floatingPositions[widgetId] || { x: 200, y: 150, z: 15 }
        const isSmall = widget.dimension === "small"
        const defaultW = widget.id === "system-calculator" ? 300 : (widget.defaultWidth || (isSmall ? 320 : 480))
        const defaultH = widget.id === "system-calculator" ? 450 : (widget.defaultHeight || (isSmall ? 380 : 430))
        const size = floatingSizes[widgetId] || { w: defaultW, h: defaultH }
        const isCalc = widget.id === "system-calculator"
        const isCalcFocused = calcFocusedWidgetId === widgetId

        const handleMouseDown = (e: React.MouseEvent) => {
          handleFocusFloatingWidget(widgetId)
          if (e.button !== 0) return
          const target = e.target as HTMLElement
          if (target.closest("button") || target.closest("input") || target.closest("textarea") || target.closest("select")) return

          const startX = e.clientX
          const startY = e.clientY
          const initialX = pos.x
          const initialY = pos.y
          let rafId: number | null = null

          document.body.classList.add("dragging")

          const handleMouseMove = (moveEvent: MouseEvent) => {
            if (rafId !== null) cancelAnimationFrame(rafId)
            rafId = requestAnimationFrame(() => {
              const deltaX = moveEvent.clientX - startX
              const deltaY = moveEvent.clientY - startY
              const winW = typeof window !== "undefined" ? window.innerWidth : 1280
              const winH = typeof window !== "undefined" ? window.innerHeight : 800
              const currentW = size.w
              const currentH = isMinimized ? 44 : size.h

              const minX = 8
              const maxX = Math.max(minX, winW - currentW - 8)
              const minY = 106
              const maxY = Math.max(minY, winH - currentH - 74)

              const nextX = Math.max(minX, Math.min(maxX, initialX + deltaX))
              const nextY = Math.max(minY, Math.min(maxY, initialY + deltaY))
              handleDragWidget(widgetId, nextX, nextY)
            })
          }

          const handleMouseUp = () => {
            if (rafId !== null) cancelAnimationFrame(rafId)
            window.removeEventListener("mousemove", handleMouseMove)
            window.removeEventListener("mouseup", handleMouseUp)
            document.body.style.userSelect = ""
            document.body.style.cursor = ""
            document.body.classList.remove("dragging")
          }

          document.body.style.userSelect = "none"
          window.addEventListener("mousemove", handleMouseMove)
          window.addEventListener("mouseup", handleMouseUp)
        }

        const handleResizeMouseDown = (e: React.MouseEvent<HTMLDivElement>, direction: string) => {
          e.preventDefault()
          e.stopPropagation()
          handleFocusFloatingWidget(widgetId)

          const el = e.currentTarget
          el.setPointerCapture((e.nativeEvent as PointerEvent).pointerId ?? 1)
          document.body.classList.add("resizing")

          const startX = e.clientX
          const startY = e.clientY
          const initialW = size.w
          const initialH = size.h
          const initialPosX = pos.x
          const initialPosY = pos.y

          let rafId: number | null = null
          let active = true

          const onPointerMove = (moveEvent: PointerEvent) => {
            if (!active) return
            if (rafId !== null) cancelAnimationFrame(rafId)
            rafId = requestAnimationFrame(() => {
              const dx = moveEvent.clientX - startX
              const dy = moveEvent.clientY - startY
              const winW = typeof window !== "undefined" ? window.innerWidth : 1280
              const winH = typeof window !== "undefined" ? window.innerHeight : 800

              const minX = 8
              const minY = 106
              const maxAvailableWFromLeft = Math.max(260, initialPosX + initialW - minX)
              const maxAvailableWFromRight = Math.max(260, winW - initialPosX - 8)
              const maxAvailableHFromTop = Math.max(200, initialPosY + initialH - minY)
              const maxAvailableHFromBottom = Math.max(200, winH - initialPosY - 74)

              let newW = initialW
              let newH = initialH
              let newX = initialPosX
              let newY = initialPosY

              if (direction.includes("e")) {
                newW = Math.max(260, Math.min(maxAvailableWFromRight, initialW + dx))
              }
              if (direction.includes("s")) {
                newH = Math.max(200, Math.min(maxAvailableHFromBottom, initialH + dy))
              }
              if (direction.includes("w")) {
                const targetW = Math.max(260, Math.min(maxAvailableWFromLeft, initialW - dx))
                newX = initialPosX + (initialW - targetW)
                newW = targetW
              }
              if (direction.includes("n")) {
                const targetH = Math.max(200, Math.min(maxAvailableHFromTop, initialH - dy))
                newY = initialPosY + (initialH - targetH)
                newH = targetH
              }

              setFloatingSizes(prev => ({ ...prev, [widgetId]: { w: newW, h: newH } }))
              if (direction.includes("w") || direction.includes("n")) {
                setFloatingPositions(prev => ({
                  ...prev,
                  [widgetId]: { ...prev[widgetId], x: newX, y: newY }
                }))
              }
            })
          }

          const cleanup = () => {
            if (!active) return
            active = false
            if (rafId !== null) cancelAnimationFrame(rafId)
            el.removeEventListener("pointermove", onPointerMove)
            el.removeEventListener("pointerup", cleanup)
            el.removeEventListener("pointercancel", cleanup)
            try { el.releasePointerCapture((e.nativeEvent as PointerEvent).pointerId ?? 1) } catch { }
            document.body.style.userSelect = ""
            document.body.style.cursor = ""
            document.body.classList.remove("resizing")
          }

          document.body.style.userSelect = "none"
          el.addEventListener("pointermove", onPointerMove)
          el.addEventListener("pointerup", cleanup)
          el.addEventListener("pointercancel", cleanup)
        }

        const handleResetSize = (e: React.MouseEvent) => {
          e.stopPropagation()
          setFloatingSizes(prev => {
            const next = { ...prev }
            delete next[widgetId]
            return next
          })
        }

        const isMinimized = minimizedWidgets.has(widgetId)
        const winW = typeof window !== "undefined" ? window.innerWidth : 1280
        const winH = typeof window !== "undefined" ? window.innerHeight : 800
        const boundedX = Math.max(8, Math.min(winW - size.w - 8, pos.x))
        const boundedY = Math.max(106, Math.min(winH - (isMinimized ? 44 : size.h) - 74, pos.y))

        return (
          <div
            key={widgetId}
            onMouseDownCapture={() => {
              handleFocusFloatingWidget(widgetId)
              if (isCalc) setCalcFocusedWidgetId(widgetId)
            }}
            onClick={() => {
              handleFocusFloatingWidget(widgetId)
              if (isCalc) setCalcFocusedWidgetId(widgetId)
            }}
            className={`fixed flex flex-col bg-white/65 dark:bg-zinc-950/55 backdrop-blur-2xl border shadow-2xl rounded-2xl overflow-hidden transition-all duration-200 ${isCalcFocused
              ? "border-[#004EB1]/50 dark:border-blue-500/50 shadow-blue-500/15 ring-1 ring-[#004EB1]/25"
              : "border-white/30 dark:border-zinc-800/60"
              }`}
            style={{
              left: `${boundedX}px`,
              top: `${boundedY}px`,
              width: `${size.w}px`,
              height: isMinimized ? "auto" : `${size.h}px`,
              maxWidth: "calc(100vw - 16px)",
              maxHeight: isMinimized ? "auto" : "calc(100vh - 134px)",
              zIndex: typeof pos.z === "number" ? Math.min(35, Math.max(15, pos.z)) : 15,
            }}
          >
            {!isMinimized && (
              <>
                <div onPointerDown={e => handleResizeMouseDown(e, "n")} className="absolute top-0 left-3 right-3 h-1 cursor-n-resize z-50 select-none touch-none" />
                <div onPointerDown={e => handleResizeMouseDown(e, "s")} className="absolute bottom-0 left-3 right-3 h-1 cursor-s-resize z-50 select-none touch-none" />
                <div onPointerDown={e => handleResizeMouseDown(e, "e")} className="absolute top-3 bottom-3 right-0 w-1 cursor-e-resize z-50 select-none touch-none" />
                <div onPointerDown={e => handleResizeMouseDown(e, "w")} className="absolute top-3 bottom-3 left-0 w-1 cursor-w-resize z-50 select-none touch-none" />
                <div onPointerDown={e => handleResizeMouseDown(e, "nw")} className="absolute top-0 left-0 size-3 cursor-nw-resize z-50 select-none touch-none" />
                <div onPointerDown={e => handleResizeMouseDown(e, "ne")} className="absolute top-0 right-0 size-3 cursor-ne-resize z-50 select-none touch-none" />
                <div onPointerDown={e => handleResizeMouseDown(e, "sw")} className="absolute bottom-0 left-0 size-3 cursor-sw-resize z-50 select-none touch-none" />
                <div onPointerDown={e => handleResizeMouseDown(e, "se")} className="absolute bottom-0 right-0 size-3 cursor-se-resize z-50 select-none touch-none group">
                  <div className="absolute bottom-1.5 right-1.5 grid grid-cols-2 gap-px opacity-30 group-hover:opacity-70 transition-opacity">
                    <div className="size-1 rounded-sm bg-zinc-400 dark:bg-zinc-500" />
                    <div className="size-1 rounded-sm bg-zinc-400 dark:bg-zinc-500" />
                    <div className="size-1 rounded-sm bg-zinc-400 dark:bg-zinc-500" />
                    <div className="size-1 rounded-sm bg-zinc-400 dark:bg-zinc-500" />
                  </div>
                </div>
              </>
            )}

            {/* Cabeçalho do Widget */}
            <div
              onMouseDown={handleMouseDown}
              className="flex items-center justify-between px-3.5 py-2 bg-zinc-100/60 dark:bg-zinc-900/60 border-b border-zinc-200/80 dark:border-zinc-800/80 cursor-move select-none shrink-0"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-base select-none shrink-0">{widget.icon}</span>
                <span className="text-xs font-bold text-zinc-800 dark:text-zinc-100 font-sans truncate">
                  {widget.name}
                </span>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  title={isMinimized ? "Restaurar" : "Minimizar"}
                  onClick={(e) => { e.stopPropagation(); handleMinimizeWidget(widgetId) }}
                  className="size-5 flex items-center justify-center rounded hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors cursor-pointer"
                >
                  {isMinimized ? <ChevronDown className="size-3" /> : <Minus className="size-3" />}
                </button>

                {!isMinimized && (
                  <button
                    type="button"
                    title="Restaurar tamanho padrão"
                    onClick={handleResetSize}
                    className="size-5 flex items-center justify-center rounded hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors cursor-pointer"
                  >
                    <Maximize2 className="size-3" />
                  </button>
                )}

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleToggleFloatingWidget(widgetId)
                  }}
                  className="flex size-5 items-center justify-center rounded-full hover:bg-red-100 dark:hover:bg-red-950/30 text-zinc-400 hover:text-red-500 dark:hover:text-red-400 transition-colors cursor-pointer"
                >
                  <X className="size-3" />
                </button>
              </div>
            </div>

            {/* Conteúdo do Widget */}
            {!isMinimized && (
              <div className={`flex-1 overflow-y-auto relative transition-all duration-200 ${isCalc ? "p-0 scrollbar-none" : "p-4 scrollbar-thin"}`}
                onClick={() => {
                  if (isCalc) setCalcFocusedWidgetId(widgetId)
                }}
              >
                {widget.id === "system-calendar" && (
                  <CalendarApp
                    calendarDate={calendarDate}
                    setCalendarDate={setCalendarDate}
                    isWidget
                  />
                )}

                {widget.id === "system-weather" && (
                  <FloatingWeatherWidget />
                )}

                {widget.id === "system-calculator" && (
                  <CalculatorWidget
                    calcFormula={calcFormula}
                    setCalcFormula={setCalcFormula}
                    calcDisplay={calcDisplay}
                    setCalcDisplay={setCalcDisplay}
                    showCalcHistory={showCalcHistory}
                    setShowCalcHistory={setShowCalcHistory}
                    calcHistory={calcHistory}
                    setCalcHistory={setCalcHistory}
                    setCalcResetOnNext={setCalcResetOnNext}
                    handleCalcPress={handleCalcPress}
                  />
                )}

                {widget.id === "system-notes" && (
                  <div className="flex flex-col h-full">
                    <textarea
                      value={notesContent}
                      onChange={(e) => setNotesContent(e.target.value)}
                      placeholder="Escreva suas anotações aqui..."
                      className="flex-1 w-full h-full p-3.5 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-amber-50/5 dark:bg-zinc-950 text-xs font-sans leading-relaxed focus:outline-none focus:ring-1 focus:ring-[#004EB1] resize-none text-zinc-850 dark:text-zinc-200"
                    />
                    <div className="flex items-center justify-between mt-2 px-1 text-[9px] text-zinc-400 dark:text-zinc-500 font-sans">
                      <span>Anotações persistentes automáticas</span>
                      <span>{notesContent.length} caracteres</span>
                    </div>
                  </div>
                )}

                {widget.type === "developer" && widget.developerType === "iframe" && (
                  <div className="w-full h-full rounded-xl overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950">
                    <iframe
                      src={widget.iframeUrl}
                      data-widget-id={widget.id}
                      className="w-full h-full border-0"
                      sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads allow-downloads-without-user-activation"
                      allow="camera; microphone; fullscreen; display-capture; autoplay; clipboard-read; clipboard-write; web-share; downloads; downloads-without-user-activation"
                      title={widget.name}
                      onLoad={(e) => {
                        try {
                          const currentTheme = (theme || (typeof window !== "undefined" && document.documentElement.classList.contains("dark") ? "dark" : "light")) === "dark" ? "dark" : "light"
                          const currentLang = lang || (typeof window !== "undefined" ? localStorage.getItem("language") || "pt-BR" : "pt-BR")
                          const cw = e.currentTarget.contentWindow
                          if (cw) {
                            cw.postMessage({ type: "BOLD_WIDGET_THEME_CHANGED", payload: currentTheme, theme: currentTheme }, "*")
                            cw.postMessage({ type: "BOLD_THEME_CHANGED", payload: currentTheme, theme: currentTheme }, "*")
                            cw.postMessage({ type: "BOLD_WIDGET_LANGUAGE_CHANGED", payload: currentLang, language: currentLang, lang: currentLang }, "*")
                            cw.postMessage({ type: "BOLD_LANGUAGE_CHANGED", payload: currentLang, language: currentLang, lang: currentLang }, "*")
                            cw.postMessage({ type: "BOLD_CONTEXT_CHANGED", payload: { theme: currentTheme, language: currentLang }, theme: currentTheme, language: currentLang, lang: currentLang }, "*")
                            cw.postMessage({ type: "BOLD_WIDGET_CONTEXT_CHANGED", payload: { theme: currentTheme, language: currentLang }, theme: currentTheme, language: currentLang, lang: currentLang }, "*")
                            cw.postMessage({ type: "BOLD_WIDGET_CONTEXT_UPDATE", payload: { theme: currentTheme, language: currentLang }, theme: currentTheme, language: currentLang, lang: currentLang }, "*")
                            cw.postMessage({ type: "BOLD_CONTEXT_UPDATE", payload: { theme: currentTheme, language: currentLang }, theme: currentTheme, language: currentLang, lang: currentLang }, "*")
                          }
                        } catch (err) {
                          // ignore
                        }
                      }}
                    />
                  </div>
                )}

                {widget.type === "developer" && widget.developerType === "code" && (
                  <div className="w-full h-full rounded-xl overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950">
                    <iframe
                      data-widget-id={widget.id}
                      title={widget.name}
                      srcDoc={buildWidgetSrcDoc(widget.code || "", {
                        theme: (theme === "dark" ? "dark" : "light") as "light" | "dark",
                        widgetId: widget.id,
                      })}
                      className="w-full h-full border-0"
                      sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads allow-downloads-without-user-activation"
                      allow="camera; microphone; fullscreen; display-capture; autoplay; clipboard-read; clipboard-write; web-share; downloads; downloads-without-user-activation"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </>
  )
}

function FloatingWeatherWidget() {
  const { weatherData, isLoading, error, isLocationGranted, requestLocation } = useWeather("pt")

  if (!isLocationGranted || !weatherData) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-3 text-center text-zinc-800 dark:text-zinc-100 font-sans space-y-2 select-none">
        <div className="size-10 rounded-xl bg-blue-500/10 dark:bg-blue-500/20 text-[#004EB1] dark:text-blue-400 flex items-center justify-center shadow-xs animate-bounce">
          <MapPin className="size-5 text-[#004EB1] dark:text-blue-400" />
        </div>
        <div>
          <h4 className="text-xs font-bold text-zinc-900 dark:text-white">Clima em Tempo Real</h4>
          <p className="text-[10px] text-zinc-500 dark:text-zinc-400 mt-0.5 max-w-[200px] leading-tight">
            Compartilhe sua localização para exibir o clima da sua região.
          </p>
        </div>
        {error && (
          <p className="text-[9px] text-red-500 max-w-[200px] bg-red-50 dark:bg-red-950/40 p-1.5 rounded-md leading-tight">
            {error}
          </p>
        )}
        <button
          type="button"
          disabled={isLoading}
          onClick={() => requestLocation(true)}
          className="px-3 py-1.5 rounded-lg bg-[#004EB1] hover:bg-[#003d8d] text-white text-[10px] font-bold transition-all shadow-xs cursor-pointer active:scale-95 disabled:opacity-50 flex items-center gap-1 mx-auto"
        >
          {isLoading && <RefreshCw className="size-3 animate-spin" />}
          <span>{isLoading ? "Buscando..." : "Ativar Localização"}</span>
        </button>
      </div>
    )
  }

  const hourlyList =
    weatherData.hourly && weatherData.hourly.length > 0
      ? weatherData.hourly.slice(0, 4)
      : [
        { time: "Agora", emoji: weatherData.conditionEmoji, temp: weatherData.currentTemp },
        { time: "+1h", emoji: weatherData.conditionEmoji, temp: weatherData.currentTemp },
        { time: "+2h", emoji: weatherData.conditionEmoji, temp: weatherData.currentTemp },
        { time: "+3h", emoji: weatherData.conditionEmoji, temp: weatherData.currentTemp },
      ]

  return (
    <div className="h-full relative overflow-hidden flex flex-col justify-between text-zinc-800 dark:text-zinc-100 select-none p-0.5">
      <div className="flex items-start justify-between">
        <div className="min-w-0 pr-2">
          <h4 className="text-sm font-bold tracking-tight text-zinc-900 dark:text-white font-sans truncate flex items-center gap-1">
            <MapPin className="size-3 text-[#004EB1] dark:text-blue-400 shrink-0" />
            <span className="truncate">{weatherData.cityName}</span>
          </h4>
          <p className="text-[10px] text-zinc-500 dark:text-zinc-400 font-sans mt-0.5 truncate">
            {weatherData.conditionLabel}
          </p>
        </div>
        <span className="text-2xl select-none shrink-0" title={weatherData.conditionLabel}>
          {weatherData.conditionEmoji}
        </span>
      </div>

      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-3xl font-extralight tracking-tighter text-zinc-900 dark:text-white font-sans">
          {weatherData.currentTemp}°
        </span>
        <span className="text-[11px] text-zinc-500 dark:text-zinc-400 font-sans">
          Mín: {weatherData.minTempToday}° • Máx: {weatherData.maxTempToday}°
        </span>
      </div>

      <div className="grid grid-cols-4 gap-1 mt-2 pt-2 border-t border-zinc-200/50 dark:border-zinc-800/50">
        {hourlyList.map((item, idx) => (
          <div key={idx} className="flex flex-col items-center gap-0.5">
            <span className="text-[9px] text-zinc-500 dark:text-zinc-400 font-sans">{item.time}</span>
            <span className="text-sm select-none">{item.emoji}</span>
            <span className="text-[10px] font-bold text-zinc-800 dark:text-zinc-200 font-sans">{item.temp}°</span>
          </div>
        ))}
      </div>
    </div>
  )
}

